import React, { useState, useEffect, useCallback } from 'react';
import { House, ShieldCheck, Plus, CircleNotch, CheckCircle, Warning, Trash, X } from '@phosphor-icons/react';
import { toast } from '../../hooks/use-toast';
import { clientEscrowAPI } from '../../api/api';
import { openCheckout } from '../../utils/checkout';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700 border-amber-400',
  funded: 'bg-blue-100 text-blue-700 border-blue-400',
  partially_released: 'bg-purple-100 text-purple-700 border-purple-400',
  released: 'bg-emerald-100 text-emerald-700 border-emerald-400',
  disputed: 'bg-red-100 text-red-700 border-red-400',
};

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const CreateModal = ({ onClose, onCreated }) => {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ homeowner_id: '', title: '', amount: '', description: '' });
  const [milestones, setMilestones] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clientEscrowAPI.eligibleClients().then(({ data }) => setClients(data.clients || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.homeowner_id) return toast({ title: 'Pick a client', variant: 'destructive' });
    if (form.title.trim().length < 3) return toast({ title: 'Give the escrow a title', variant: 'destructive' });
    if (!(parseFloat(form.amount) > 0)) return toast({ title: 'Enter a valid amount', variant: 'destructive' });
    setSaving(true);
    try {
      const client = clients.find((c) => c.homeowner_id === form.homeowner_id);
      await clientEscrowAPI.create({
        ...form,
        amount: parseFloat(form.amount),
        quote_request_id: client?.quote_request_id,
        milestones: milestones.filter((m) => m.name && parseFloat(m.amount) > 0).map((m) => ({ name: m.name, amount: parseFloat(m.amount) })),
      });
      toast({ title: 'Escrow proposal sent!', description: 'Your client was notified to fund it.' });
      onCreated();
      onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to create escrow', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-black w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="client-escrow-create-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-black text-lg text-neutral-900 dark:text-white">Propose Client Escrow</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center justify-center" data-testid="client-escrow-modal-close"><X size={16} weight="bold" /></button>
        </div>
        <div className="space-y-3">
          <select value={form.homeowner_id} onChange={(e) => setForm((f) => ({ ...f, homeowner_id: e.target.value }))}
            className="w-full h-11 px-3 rounded-xl border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm font-medium dark:text-white" data-testid="client-escrow-client-select">
            <option value="">Select client (accepted quotes)...</option>
            {clients.map((c) => <option key={c.homeowner_id} value={c.homeowner_id}>{c.name} — {c.project}</option>)}
          </select>
          {clients.length === 0 && <p className="text-xs text-neutral-500">No accepted quotes yet — clients appear here once they accept one of your quotes.</p>}
          <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title — e.g. Kitchen Renovation payment protection"
            className="w-full h-11 px-3.5 rounded-xl border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm dark:text-white" data-testid="client-escrow-title-input" />
          <input type="number" min="1" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="Total amount (USD)"
            className="w-full h-11 px-3.5 rounded-xl border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm dark:text-white" data-testid="client-escrow-amount-input" />
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold uppercase text-neutral-400">Milestones (optional)</span>
              <button onClick={() => setMilestones((m) => [...m, { name: '', amount: '' }])} className="text-xs font-bold text-[#2B4C6F] flex items-center gap-1" data-testid="add-milestone-btn"><Plus size={12} weight="bold" /> Add</button>
            </div>
            {milestones.map((m, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={m.name} onChange={(e) => setMilestones((ms) => ms.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder={`Milestone ${i + 1}`}
                  className="flex-1 h-10 px-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm dark:text-white" data-testid={`milestone-name-${i}`} />
                <input type="number" min="1" value={m.amount} onChange={(e) => setMilestones((ms) => ms.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} placeholder="$"
                  className="w-24 h-10 px-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm dark:text-white" data-testid={`milestone-amount-${i}`} />
                <button onClick={() => setMilestones((ms) => ms.filter((_, j) => j !== i))} className="w-10 h-10 rounded-lg text-neutral-400 hover:text-red-500 flex items-center justify-center"><Trash size={15} weight="bold" /></button>
              </div>
            ))}
          </div>
          <button onClick={submit} disabled={saving}
            className="w-full h-12 rounded-xl bg-[#2B4C6F] text-white font-bold border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none disabled:opacity-60 flex items-center justify-center gap-2" data-testid="client-escrow-submit-btn">
            {saving ? <CircleNotch size={18} className="animate-spin" /> : <ShieldCheck size={18} weight="fill" />} Send to Client
          </button>
        </div>
      </div>
    </div>
  );
};

export const ClientEscrowsSection = ({ role }) => {
  const [escrows, setEscrows] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    clientEscrowAPI.mine().then(({ data }) => setEscrows(data.escrows || [])).catch(() => setEscrows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Confirm funding after Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fundedId = params.get('client_escrow_funded');
    if (fundedId && role === 'homeowner') {
      clientEscrowAPI.confirmFunding(fundedId)
        .then(() => { toast({ title: 'Escrow funded!', description: 'Funds are protected — release them as milestones complete.' }); load(); })
        .catch((e) => toast({ title: 'Funding check failed', description: e.response?.data?.detail || 'Please refresh', variant: 'destructive' }));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [role, load]);

  const fund = async (id) => {
    setBusy(id);
    try {
      const { data } = await clientEscrowAPI.fund(id);
      openCheckout(data.checkout_url);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Could not start checkout', variant: 'destructive' });
      setBusy(null);
    }
  };

  const release = async (id, milestoneId = null) => {
    if (!window.confirm(milestoneId ? 'Release this milestone payment to your contractor?' : 'Release ALL remaining funds to your contractor?')) return;
    setBusy(id);
    try {
      const { data } = await clientEscrowAPI.release(id, milestoneId ? { milestone_id: milestoneId } : {});
      toast({ title: 'Payment released!', description: `${fmt(data.released)} sent to your contractor.` });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Release failed', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this escrow proposal?')) return;
    try { await clientEscrowAPI.cancel(id); load(); } catch { toast({ title: 'Cancel failed', variant: 'destructive' }); }
  };

  const dispute = async (id) => {
    const reason = window.prompt('Describe the issue (this opens an admin-reviewed dispute):');
    if (!reason || reason.trim().length < 5) return;
    try {
      await clientEscrowAPI.dispute(id, { reason });
      toast({ title: 'Dispute opened', description: 'Our team will review and follow up.' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Dispute failed', variant: 'destructive' });
    }
  };

  if (escrows === null) return null;
  if (role === 'homeowner' && escrows.length === 0) return null;

  return (
    <div className="mt-8" data-testid="client-escrows-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-black text-neutral-900 dark:text-white flex items-center gap-2">
          <House size={20} weight="fill" className="text-[#2563EB]" />
          {role === 'homeowner' ? 'Payment Protection (Escrow)' : 'Client Escrows'}
        </h2>
        {role === 'contractor' && (
          <button onClick={() => setShowCreate(true)}
            className="h-10 px-4 rounded-xl bg-[#2B4C6F] text-white text-sm font-bold border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none flex items-center gap-1.5" data-testid="new-client-escrow-btn">
            <Plus size={15} weight="bold" /> Propose Escrow
          </button>
        )}
      </div>

      {escrows.length === 0 ? (
        <div className="bg-white dark:bg-neutral-800 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-xl p-6 text-center text-sm text-neutral-500" data-testid="client-escrows-empty">
          Propose an escrow to a client — they fund it upfront and release payments as milestones complete.
        </div>
      ) : (
        <div className="space-y-3">
          {escrows.map((e) => (
            <div key={e._id} className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-4 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F]" data-testid={`client-escrow-card-${e._id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-black text-neutral-900 dark:text-white">{e.title}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border-2 ${STATUS_STYLES[e.status] || ''}`} data-testid={`client-escrow-status-${e._id}`}>{e.status.replace('_', ' ')}</span>
                  </div>
                  <p className="text-sm text-neutral-500 mt-0.5">
                    {role === 'homeowner' ? `Contractor: ${e.contractor_name}` : `Client: ${e.homeowner_name}`}
                    {' · '}Held {fmt(e.held_amount)} · Released {fmt(e.released_amount)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-black text-neutral-900 dark:text-white">{fmt(e.total_amount)}</p>
                </div>
              </div>

              {e.milestones?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {e.milestones.map((m) => (
                    <div key={m.id} className="flex items-center justify-between bg-neutral-50 dark:bg-neutral-900/50 rounded-lg px-3 py-2 text-sm" data-testid={`client-milestone-${m.id}`}>
                      <span className="flex items-center gap-2 dark:text-white">
                        {m.status === 'paid' ? <CheckCircle size={15} weight="fill" className="text-emerald-500" /> : <ShieldCheck size={15} weight="bold" className="text-neutral-400" />}
                        {m.name}
                      </span>
                      <span className="flex items-center gap-2 font-bold dark:text-white">
                        {fmt(m.amount)}
                        {role === 'homeowner' && m.status !== 'paid' && ['funded', 'partially_released'].includes(e.status) && (
                          <button onClick={() => release(e._id, m.id)} disabled={busy === e._id}
                            className="h-8 px-3 rounded-full bg-emerald-600 text-white text-xs font-bold disabled:opacity-60" data-testid={`release-milestone-${m.id}`}>
                            Approve & Release
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 mt-3">
                {role === 'homeowner' && e.status === 'pending' && (
                  <button onClick={() => fund(e._id)} disabled={busy === e._id}
                    className="h-10 px-5 rounded-xl bg-[#2563EB] text-white text-sm font-bold border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none disabled:opacity-60 flex items-center gap-1.5" data-testid={`fund-client-escrow-${e._id}`}>
                    {busy === e._id ? <CircleNotch size={15} className="animate-spin" /> : <ShieldCheck size={15} weight="fill" />} Fund Escrow
                  </button>
                )}
                {role === 'homeowner' && ['funded', 'partially_released'].includes(e.status) && !e.milestones?.length && (
                  <button onClick={() => release(e._id)} disabled={busy === e._id}
                    className="h-10 px-5 rounded-xl bg-emerald-600 text-white text-sm font-bold border-2 border-black shadow-[2px_2px_0px_#000] disabled:opacity-60" data-testid={`release-all-${e._id}`}>
                    Release Funds
                  </button>
                )}
                {['funded', 'partially_released'].includes(e.status) && (
                  <button onClick={() => dispute(e._id)} className="h-10 px-4 rounded-xl text-sm font-bold text-red-600 border-2 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-1" data-testid={`dispute-client-escrow-${e._id}`}>
                    <Warning size={14} weight="bold" /> Dispute
                  </button>
                )}
                {role === 'contractor' && e.status === 'pending' && (
                  <button onClick={() => cancel(e._id)} className="h-10 px-4 rounded-xl text-sm font-bold text-neutral-500 border-2 border-neutral-200 dark:border-neutral-600 hover:text-red-500" data-testid={`cancel-client-escrow-${e._id}`}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
};

export default ClientEscrowsSection;
